import { apiService, extractApiErrorMessage } from '../services/apiService';
import { assertFunctionReferenceSyntax } from '../services/functionService';
import { proxyService } from '../services/proxyService';
import type { AgentMessageResponse } from '../types/agent';
import { prepareAgentUploadBodyForProxy } from './agentUploadBody';

function serializeProxyBody(data: unknown): string {
  if (data == null) return '';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

function assertAgentOutboundFunctionSyntax(
  url: string,
  headers: Record<string, string>,
  texts: Array<string | null | undefined>,
): void {
  assertFunctionReferenceSyntax(url);
  for (const value of Object.values(headers)) {
    if (value != null) {
      assertFunctionReferenceSyntax(String(value));
    }
  }
  for (const text of texts) {
    if (typeof text === 'string' && text.includes('$[')) {
      assertFunctionReferenceSyntax(text);
    }
  }
}

function failureAgentReply(message: string): AgentMessageResponse {
  return {
    reply: message,
    error: message,
    actions: [],
  };
}

/**
 * Execute a single agent request through the browser proxy (same path as Collections Send).
 * The backend only prepares the request and evaluates assertions on the client result.
 */
export async function runAgentSingleRequestViaClientProxy(
  payload: Record<string, unknown>,
  options?: { chatId?: number; message?: string },
): Promise<AgentMessageResponse> {
  const preparePayload = { ...payload };
  delete preparePayload.clientExecution;
  const requestIdRaw = preparePayload.requestId;
  const requestId =
    typeof requestIdRaw === 'number'
      ? requestIdRaw
      : typeof requestIdRaw === 'string' && requestIdRaw.trim() !== ''
        ? Number(requestIdRaw)
        : null;

  let spec: Awaited<ReturnType<typeof apiService.agentPrepareSingleRequest>>;
  try {
    spec = await apiService.agentPrepareSingleRequest(preparePayload);
  } catch (err: unknown) {
    // Prepare returns 400 for missing path params / unknown request, etc.
    // Fall back to /agent/message so the chat gets a guided reply instead of an uncaught Axios error.
    try {
      return await apiService.agentMessage({
        action: 'run_single_request',
        chatId: options?.chatId,
        message: options?.message,
        payload: preparePayload,
      });
    } catch (messageErr: unknown) {
      const message =
        extractApiErrorMessage(err) ||
        extractApiErrorMessage(messageErr) ||
        (err instanceof Error ? err.message : 'Failed to prepare request for execution.');
      return failureAgentReply(message);
    }
  }

  const start = Date.now();
  let clientExecution: Record<string, unknown>;
  // proxyService.sendRequest already writes one Console row, same as Collections Send.
  let loggedByBrowserProxy = false;
  try {
    const url = spec.url?.trim();
    if (!url) {
      throw new Error(
        'Prepared request is missing a target URL. Check the environment base URL and request path.',
      );
    }
    const method = (spec.method || 'GET').toUpperCase();
    const hasBody =
      spec.body != null &&
      spec.body !== '' &&
      (method === 'POST' || method === 'PUT' || method === 'PATCH');

    let bodySource = hasBody ? spec.body : null;
    let bodyType = spec.bodyType;
    let preparedUpload = prepareAgentUploadBodyForProxy({
      body: hasBody ? bodySource : null,
      bodyType,
      headers: spec.headers ?? {},
      requestId,
    });

    if (
      hasBody &&
      !preparedUpload.rebuilt &&
      requestId != null &&
      !Number.isNaN(requestId)
    ) {
      const normalizedType = String(bodyType ?? '').toLowerCase();
      const needsUploadRebuild =
        normalizedType.includes('form') ||
        normalizedType === 'binary' ||
        (typeof bodySource === 'string' && bodySource.includes('__fileBlob'));
      if (needsUploadRebuild) {
        try {
          const fresh = await apiService.getRequest(requestId);
          const freshBody =
            fresh.body != null
              ? typeof fresh.body === 'string'
                ? fresh.body
                : JSON.stringify(fresh.body)
              : null;
          if (freshBody) {
            bodySource = freshBody;
            bodyType = fresh.bodyType ?? bodyType;
            preparedUpload = prepareAgentUploadBodyForProxy({
              body: freshBody,
              bodyType,
              headers: spec.headers ?? {},
              requestId,
            });
          }
        } catch {
          /* use prepare response */
        }
      }
    }

    if (hasBody && !preparedUpload.rebuilt) {
      const normalizedType = String(bodyType ?? '').toLowerCase();
      if (
        normalizedType.includes('form') ||
        normalizedType === 'binary' ||
        (typeof bodySource === 'string' && bodySource.includes('__fileBlob'))
      ) {
        throw new Error(
          'Could not rebuild the uploaded file for this request. Open it in Collections, re-save the file attachment, then retry from the AI Agent.',
        );
      }
    }

    assertAgentOutboundFunctionSyntax(url, preparedUpload.headers, [
      typeof preparedUpload.body === 'string' ? preparedUpload.body : null,
      typeof bodySource === 'string' ? bodySource : null,
    ]);

    loggedByBrowserProxy = true;
    const proxyRes = await proxyService.sendRequest({
      method,
      url,
      headers: preparedUpload.headers,
      body: hasBody ? preparedUpload.body : undefined,
    });
    clientExecution = {
      statusCode: proxyRes.status,
      body: serializeProxyBody(proxyRes.data),
      headers: proxyRes.headers ?? {},
      executionTimeMs: Date.now() - start,
      errorMessage:
        proxyRes.status >= 200 && proxyRes.status < 300
          ? null
          : proxyRes.statusText || `HTTP ${proxyRes.status}`,
    };
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : typeof err === 'string' ? err : 'Request execution failed';
    // Preserve function errors so the agent shows them instead of a later 403/auth failure.
    const functionDiagnostic = /unknown function|invalid function reference/i.test(message);
    clientExecution = {
      statusCode: 0,
      body: functionDiagnostic ? message : '',
      headers: {},
      executionTimeMs: Date.now() - start,
      errorMessage: message,
    };
  }

  try {
    const res = await apiService.agentMessage({
      action: 'run_single_request',
      chatId: options?.chatId,
      message: options?.message,
      payload: {
        ...preparePayload,
        clientExecution,
      },
    });
    if (loggedByBrowserProxy && res?.proxyLogs?.length) {
      return { ...res, proxyLogs: undefined };
    }
    return res;
  } catch (err: unknown) {
    const message =
      extractApiErrorMessage(err) ||
      (err instanceof Error ? err.message : 'Request executed but the agent reply failed.');
    return failureAgentReply(message);
  }
}
